import { atom, type Setter } from "jotai";

import { getCurrentUser, getUserMenuTree } from "@/api/auth";
import { OPS_SYSTEM_POINT } from "@/config/system";
import { mapMenuTreeToNavItems } from "@/routes/menuConfig";
import { bootstrapAtom, currentUserAtom, menusAtom, permissionsAtom } from "@/store/auth";
import { initDictionariesAtom } from "@/store/dict";
import { clearToken, getToken, mapCurrentUser } from "@/utils";

/** runBootstrap 可选参数 */
export interface RunBootstrapOptions {
  /**
   * 是否拉取初始化接口（用户 / 菜单 / 权限 / 字典）
   * - true：登录成功后强制拉取（即使当前仍在 /login）
   * - 未传：有 token 且不在登录页时才拉取（如刷新已登录业务页）
   */
  fetchAuth?: boolean;
}

/** 当前路由是否为登录页 */
const isLoginPath = () => window.location.pathname.startsWith("/login");

/**
 * 判断是否应请求初始化接口
 * @param options runBootstrap 入参
 */
const shouldFetchAuthData = (options?: RunBootstrapOptions): boolean => {
  if (!getToken()) return false;
  if (options?.fetchAuth === true) return true;
  if (options?.fetchAuth === false) return false;
  // 默认：已登录且不在登录页（例如浏览器刷新业务页）
  return !isLoginPath();
};

/** 清空鉴权数据并进入 guest，仅在 session 仍有效时写入 */
const applyGuestState = (set: Setter, session: number, isStale: () => boolean) => {
  if (isStale()) return;
  set(currentUserAtom, null);
  set(menusAtom, []);
  set(permissionsAtom, []);
  set(bootstrapAtom, { status: "guest", session });
};

/** 拉取用户、菜单、权限，并后台加载字典 */
const fetchAuthInitData = async (set: Setter, session: number, isStale: () => boolean) => {
  const [userRes, menuRes] = await Promise.all([
    getCurrentUser(),
    getUserMenuTree({ systemPoint: OPS_SYSTEM_POINT }),
  ]);
  if (isStale()) return;

  set(currentUserAtom, mapCurrentUser(userRes.data));
  set(menusAtom, mapMenuTreeToNavItems(menuRes.data.menus));
  set(permissionsAtom, menuRes.data.permissions);
  set(bootstrapAtom, { status: "ready", session });
  void set(initDictionariesAtom);
};

/**
 * 执行 bootstrap
 * - 登录页或未登录：不调初始化接口，进入 guest（由路由守卫跳转登录页）
 * - 登录成功（fetchAuth: true）或已登录访问业务页：拉取初始化接口
 */
export const runBootstrapAtom = atom(null, async (get, set, options?: RunBootstrapOptions) => {
  const nextSession = get(bootstrapAtom).session + 1;
  set(bootstrapAtom, { status: "loading", session: nextSession });
  const isStale = () => get(bootstrapAtom).session !== nextSession;

  // 登录页或未登录：不请求任何初始化接口
  if (!shouldFetchAuthData(options)) {
    applyGuestState(set, nextSession, isStale);
    return;
  }

  try {
    await fetchAuthInitData(set, nextSession, isStale);
  } catch {
    if (isStale()) return;
    // 登录失效 / bootstrap 失败：清 token，guest 后由 ProtectedRoute 或 401 拦截跳转登录页
    clearToken();
    applyGuestState(set, nextSession, isStale);
  }
});

/**
 * 登录成功后调用：写入 token 后 dispatch，强制拉取初始化接口
 */
export const bumpAuthSessionAtom = atom(null, (_get, set) => {
  void set(runBootstrapAtom, { fetchAuth: true });
});
